import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, of, map, tap } from 'rxjs';
import { UserClientAccessScopeState } from '../models/card-quota/user-client-access.model';
import { ApiCardQuotaService } from './api/card-quota/api-card-quota.service';
import { MapperUserClientAccessService } from '../mappers/card-quota/mapper-user-client-access.service';

const emptyScope = (): UserClientAccessScopeState => ({
  isRestricted: false,
  clientIds: [],
  clients: [],
});

@Injectable({ providedIn: 'root' })
export class ClientAccessScopeService {
  private readonly api = inject(ApiCardQuotaService);
  private readonly mapper = inject(MapperUserClientAccessService);
  private readonly cache = new Map<string, UserClientAccessScopeState>();

  readonly state = signal<UserClientAccessScopeState | null>(null);

  readonly isRestricted = computed(() => this.state()?.isRestricted ?? false);

  readonly isClientLocked = computed(() => {
    const s = this.state();
    return !!s?.isRestricted && s.clients.length === 1;
  });

  readonly lockedClientId = computed(() => {
    const s = this.state();
    if (!s?.isRestricted || s.clients.length !== 1) return 0;
    return s.clients[0].id;
  });

  readonly lockedClientName = computed(() => {
    const s = this.state();
    if (!s?.isRestricted || s.clients.length !== 1) return '';
    return s.clients[0].name;
  });

  readonly scopeSummaryLabel = computed(() => {
    const s = this.state();
    if (!s?.isRestricted) return '';
    if (s.clients.length === 1) return s.clients[0].name;
    if (s.clients.length > 1) return `${s.clients.length} ร้าน`;
    return 'ไม่มีร้านที่เข้าถึงได้';
  });

  load(siteId: number, userId: number, force = false): Observable<UserClientAccessScopeState> {
    if (siteId <= 0 || userId <= 0) {
      const empty = emptyScope();
      this.state.set(empty);
      return of(empty);
    }

    const key = `${siteId}:${userId}`;
    const cached = this.cache.get(key);
    if (cached && !force) {
      this.state.set(cached);
      return of(cached);
    }

    return this.api.getAccessibleClientsForUser(siteId, userId).pipe(
      map((res) => this.mapper.mapAccessibleClients(res)),
      tap({
        next: (mapped) => {
          this.cache.set(key, mapped);
          this.state.set(mapped);
        },
        error: () => {
          const fallback = emptyScope();
          this.state.set(fallback);
        },
      })
    );
  }

  clear(): void {
    this.cache.clear();
    this.state.set(null);
  }
}
